import { createContext, ReactNode, useState, useMemo, useEffect } from "react";
import React, { useContext } from "react";
import * as authApi from "../api/auth.tsx";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

type UserObject = {
  user_id: number;
  first_name: string;
  last_name: string;
  user_type: string;
  view_access_list: {
    school_types: string;
    district: string;
    isWrite: boolean;
    isEvaluator: boolean;
    isIndividualSchool: boolean;
    schoolID: number;
    smartclass: boolean;
    mathlab: boolean;
  };
  auth_token: string;
};

interface UserContextType {
  user: UserObject;
  loading: boolean;
  error?: any;
  login: (user_id: string, password: string) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType>({} as UserContextType);

export function UserContextProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [user, setUser] = useState<UserObject>();
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      setUser(JSON.parse(user));
    }
    setLoadingInitial(false);
  }, []);

  const login = (user_id: string, password: string) => {
    setLoading(true);
    authApi
      .login(user_id, password)
      .then((res) => {
        if (!res.data.user_id) {
          throw new Error(res.data.message);
        }
        const user: UserObject = {
          user_id: parseInt(res.data.user_id),
          first_name: res.data.fname,
          last_name: res.data.lname,
          user_type: res.data.user_type,
          view_access_list: JSON.parse(res.data.view_access_list),
          auth_token: res.data.auth_token,
        };
        setUser(user);
        localStorage.setItem("user", JSON.stringify(user));
        toast.success("Login successful");

        const schoolId = user.view_access_list.schoolID;
        if (
          user.view_access_list.isIndividualSchool &&
          user.view_access_list.smartclass &&
          user.view_access_list.mathlab
        ) {
          navigate(`/dashboard/school/${schoolId}`);
        } else if (
          user.view_access_list.isIndividualSchool &&
          !user.view_access_list.smartclass &&
          user.view_access_list.mathlab
        ) {
          navigate(`/mathlab/school/${user.view_access_list.schoolID}`);
        } else if (
          user.view_access_list.isIndividualSchool &&
          user.view_access_list.smartclass &&
          !user.view_access_list.mathlab
        ) {
          navigate(`/smartclass/school/${user.view_access_list.schoolID}`);
        } else {
          navigate("/");
        }
      })
      .catch((_error: any) => {
        setError(_error);
        console.log(_error);
        toast.error("Error logging in.");
      })
      .finally(() => setLoading(false));
  };

  const logout = () => {
    setUser(undefined);
    navigate("/login");
    localStorage.removeItem("user");
    sessionStorage.clear();
  };

  const memoedValue = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, error]
  );
  return (
    <UserContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </UserContext.Provider>
  );
}

export default function useAuth() {
  return useContext(UserContext);
}
